import axios from "axios";
import { instance } from "@/axios";
import * as utils from "@/utils";

//public apis - dont need auth token in header
const userLogin = function(email, password) {
  return axios.post("public/api/user/login", { email, password });
};
const signUp = function(password, userDto, userUsageClaim) {
  return axios.post("public/api/user/register", {
    password,
    userDto,
    userUsageClaim
  });
};

const verifyMailAddress = function(token) {
  return axios.post("public/api/user/verify-emailAddress", { token });
};

const activateAccount = function (token){
  return instance.post("admin/activate-UserAccount", {token});
}

const deleteAccount = function (token){
  return instance.post("admin/delete-UserAccount", {token});
}

const getUserByToken = function(token) {
    return instance.get("admin/user?token=" + token, { data: {} });
};

const renewAdminCertificate = function (adminToken, adminCertContent){
  return instance.post("admin/renew-AdminCertificate", {
    adminToken,
    adminCertContent
  });
}

const verifyEmailUpdate = function(temporaryToken) {
  return axios.post("public/api/user/verify-update-email", { token: temporaryToken });
};
const resetPassword = function(userEmail) {
  return axios.post("public/api/user/reset-password", { userEmail });
};
const verifyResetPassword = function(passwordResetToken) {
  return axios.post("public/api/user/verify-user-password-reset", { passwordResetToken });
};
const refreshToken = function() {
  return axios.post("public/api/user/refresh-jwt", { withCredentials: true });
};
const getUserUsageClaimValues = function() {
  return axios.get("public/api/user/usage-claim-values", { data: {} });
};
const resendResetPasswordEmail = function(email) {
  return axios.put("public/api/user/resend-password-reset-email", { email });
};
const resendRegistrationEmail = function(email) {
  return axios.put("public/api/user/resend-registration-email", { email });
};
//forgot password with temp auth token in the header - this token will not be available in the store
const saveResetPassword = function(newPassword, token) {
  const instance = axios.create({
    baseURL: "api/",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json"
    }
  });
  return instance.post("/current-user/save-reset-password", { newPassword });
};

//current user api#s- need auth token in the header
const getTopExpiringCertificates = function() {
  return instance.get("certificate/expiring" + "?size=10", { data: {} });
};
const getCaCertificates = function() {
  return instance.get("certificate/cas", { data: {} });
};

const downloadCaCertificate = function(id) {
  return instance.get(`certificate/ca-cert/${id}/download`, { data: {} });
};
const userLogout = function() {
  return instance.post("/current-user/logout");
};
const deleteOwnUserAccount = function() {
  return instance.delete("/current-user/", { data: {} });
};
const updateCurrentUserPassword = function(oldPassword, newPassword) {
  return instance.post("/current-user/update/password", {
    oldPassword,
    newPassword
  });
};
const getAccountDetails = function() {
  return instance.get("/current-user/");
};

const updateUserDetails = function(firstName, lastName, orgName, country) {
  return instance.post("/current-user/update/details", {
    firstName,
    lastName,
    orgName,
    country
  });
};
const updateUserEmail = function(newEmail) {
  return instance.post("/current-user/update/email", { newEmail });
};
const contactViaEmail = function(contactMessage) {
  return instance.post("/current-user/contact", { contactMessage });
};

const logout = function() {
  return instance.post("/current-user/logout", {});
};
const resendUpdateEmailVerification = function(newEmail) {
  return instance.post("/current-user/update/resend-email-update-email", { newEmail });
};
/**
 * Performs a certificate search with search criteria and pagination
 *
 * Request body: List<SpecSearchCriteria>
 * Request query params: '?page=1&size=10&sort=personName,desc'
 * Response: Page<CertificateDto>
 *
 * @param searchCriteria the search criteria as an array (e.g. '[{key: "organization", operation: "~", value: "MTG AG", logicalOperation: "AND"}, ...]')
 * @param pageable the pagination information (e.g. '{page: 1, size: 10, sort: ["personName,desc", "age,asc", ...]}')
 * @return {Promise} the certificates as a Page<CertificateDto>
 * @see https://docs.spring.io/spring-data/rest/docs/current/reference/html/#paging-and-sorting
 */
const searchCertificates = function(pageable, searchCriteria) {
  const paginationQueryParams = utils.buildPaginationQueryParams(pageable);
  // Clean up the search criteria
  const searchCriteriaDto = searchCriteria || [];
  return instance.post("certificate/user-certs/search?" + paginationQueryParams, searchCriteriaDto);
};
const getCertificateDetail = function(id) {
  return instance.get(`certificate/user-cert/detail/${id}`, { data: {} });
};
const getCertificateAudits = function(certId) {
  return instance.get(`certificate/${certId}/audit-events`, { data: {} });
};
/**
 * Request: CertificateRequestDto
 * Response: CertGenResponseDto
 */
const createCertificate = function(
  country,
  commonName,
  org,
  orgUnit,
  email,
  domainName,
  validFor,
  certUsageType,
  certCryptoType,
  keyPairMode,
  pkcs10ReqBase64,
  certPublicKeyAlgorithm,
  certSignatureAlgorithm
) {
  return instance.post("certificate/create", {
    country,
    commonName,
    org,
    orgUnit,
    email,
    domainName,
    validFor,
    certUsageType,
    certCryptoType,
    keyPairMode,
    pkcs10ReqBase64,
    certPublicKeyAlgorithm,
    certSignatureAlgorithm
  });
};
const downloadCertificate = function(id) {
  return instance.get(`certificate/user-cert/download/${id}`, { data: {} });
};
const downloadMultipleCertificates = function(ids) {
  return instance.get("certificate/user-cert/download/multiple?ids=" + ids, { data: {} });
};

const revokeCertificate = function(id) {
  return instance.post(`certificate/revoke/${id}`, {});
};
const revokeMultipleCertificates = function(ids) {
  return instance.post("certificate/revoke/multiple?ids=" + ids, {});
};
const parseBase64 = function(base64EncodedPkcs10) {
  return instance.put("certificate/parsePkcs10", { base64EncodedPkcs10 });
};
const getCertificateStatisticsForUser = function() {
  return instance.get("certificate/statistics", { data: {} });
};
const getAuditsForUser = function() {
  return instance.get("certificate/latest-audit-events", { data: {} });
};
export default {
  userLogin,
  userLogout,
  signUp,
  verifyMailAddress,
  verifyEmailUpdate,
  activateAccount,
  resetPassword,
  refreshToken,
  getTopExpiringCertificates,
  getCaCertificates,
  downloadCaCertificate,
  verifyResetPassword,
  deleteOwnUserAccount,
  updateCurrentUserPassword,
  saveResetPassword,
  getAccountDetails,
  updateUserDetails,
  updateUserEmail,
  contactViaEmail,
  searchCertificates,
  getCertificateDetail,
  createCertificate,
  parseBase64,
  downloadCertificate,
  downloadMultipleCertificates,
  revokeCertificate,
  revokeMultipleCertificates,
  getCertificateAudits,
  getCertificateStatisticsForUser,
  getAuditsForUser,
  getUserUsageClaimValues,
  logout,
  resendResetPasswordEmail,
  resendUpdateEmailVerification,
  resendRegistrationEmail,
  getUserByToken,
  deleteAccount,
  renewAdminCertificate
};
